import { useContext, useEffect, useState, Dispatch, SetStateAction } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import UserContext from "Context/UserProvider";
import AccountDashboard from "components/dashboard/AccountDashboard";
import BusinessDashboard from "components/dashboard/BusinessDashboard";
import { Box, CircularProgress, Alert, Grid } from '@mui/material';
import { useApiService } from "../../services/ApiService/ApiService";

interface DashboardPageProps {
    name: string;
    error: [boolean, Dispatch<SetStateAction<boolean>>];
}

function DashboardPage({ name, error }: DashboardPageProps) {
    const { userInfo } = useContext(UserContext);
    const { getAccessTokenSilently } = useAuth0();
    const [dashboardInfo, setDashboardInfo] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [, setError] = error;

    const { fetchWithAuth } = useApiService();

    useEffect(() => {
        const fetchDashboardInfo = async () => {
            try {
                setLoading(true);
                const token = await getAccessTokenSilently();
                const response = await fetchWithAuth('/account/dashboard/get', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        uuid: userInfo.uuid,
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    setDashboardInfo(data);
                    setError(false);
                } else {
                    setErrorMessage('Failed to load dashboard information');
                    setError(true);
                }
            } catch (error) {
                console.error('Error fetching dashboard info:', error);
                setErrorMessage('Error loading dashboard information');
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardInfo();
    }, [userInfo, setError, fetchWithAuth]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (errorMessage) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Error loading dashboard: {errorMessage}
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                width: '100%',
                height: '100%'
            }}
        >
            <Grid container spacing={3} sx={{ width: '100%', margin: 0 }}>
                <Grid item xs={12}>
                    <AccountDashboard dashboardInfo={dashboardInfo} />
                </Grid>
                <Grid item xs={12}>
                    <BusinessDashboard dashboardInfo={dashboardInfo} />
                </Grid>
            </Grid>
        </Box>
    );
}

export default DashboardPage;
